import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

// Compact SKU picker for a table cell, backed by the ERP item master
// (GET /portal/items). Selecting an item returns {icode, iname, unit} so the caller
// can auto-fill the (system-derived) description + UOM. Free-typing is still allowed.
export function ItemSelect({ value, onSelect, onText, testid = "item-select", placeholder = "Search SKU…" }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/portal/items?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (alive) setResults(r.data || []); })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (it) => {
    onSelect(it);
    setQ(it.icode);
    setOpen(false);
    setResults([]);
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        className="jde-input"
        value={q}
        placeholder={placeholder}
        data-testid={testid}
        style={{ width: "100%", minWidth: 0, padding: "5px 6px", boxSizing: "border-box" }}
        onChange={(e) => { setQ(e.target.value); onText && onText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && q.trim().length >= 2 && (
        <div data-testid={`${testid}-results`}
             style={{ position: "absolute", zIndex: 30, top: "100%", left: 0, minWidth: 340, marginTop: 2,
                      maxHeight: 240, overflowY: "auto", background: "#fff",
                      border: "1px solid var(--jde-border, #cbd5e1)", borderRadius: 6, boxShadow: "0 8px 24px rgba(15,23,42,0.16)" }}>
          {searching && <div style={{ padding: "8px 10px", fontSize: 12, color: "#64748B" }}>Searching…</div>}
          {!searching && results.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 12, color: "#64748B" }}>No matching items in the ERP master.</div>
          )}
          {results.map((it) => (
            <div key={it.icode} onClick={() => pick(it)} data-testid={`${testid}-opt-${it.icode}`}
                 style={{ padding: "7px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid #EEF1F4" }}
                 onMouseEnter={(e) => (e.currentTarget.style.background = "#F1F5F9")}
                 onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}>
              <div className="text-mono" style={{ fontWeight: 700, color: "var(--omh-primary, #1E40AF)" }}>
                {it.icode}{it.picode && it.picode !== it.icode ? ` · ${it.picode}` : ""}
              </div>
              <div style={{ color: "#475569" }}>{it.iname || "—"} <span style={{ color: "#94A3B8" }}>· {it.unit}</span></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
